/**
 * PageLoadingFallback.jsx — Fix #10b (2026-07-07 pass).
 *
 * Prominent, unmissable full-content-area loader. Renders inside every
 * Suspense boundary + as a manual fallback for routes whose child
 * fetches haven't resolved.
 *
 * Anatomy:
 *  - min-height 60vh so it can never be a tiny sliver at the top of
 *    the viewport.
 *  - 48 px indigo Loader2 (Lucide) with a slow spin.
 *  - Big caption ("Loading <route name>…") + small muted sub-caption.
 *  - Neutral paper background.
 */
import { Loader2 } from "lucide-react";

export default function PageLoadingFallback({
    label = "Loading",
    subLabel = "Just a moment",
    minHeight = "60vh",
}) {
    return (
        <div
            role="status"
            aria-live="polite"
            aria-label={label}
            data-testid="page-loading-fallback"
            style={{
                minHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--paper, #F8FAFC)",
                padding: "24px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 18,
                    padding: 48,
                    maxWidth: 440,
                    textAlign: "center",
                }}
            >
                <Loader2
                    size={48}
                    strokeWidth={1.6}
                    color="#1A237E"
                    aria-hidden="true"
                    style={{
                        animation: "coda-fallback-spin 1s linear infinite",
                    }}
                />
                <div
                    style={{
                        fontSize: 18,
                        fontFamily: "var(--font-heading, ui-sans-serif, system-ui)",
                        fontWeight: 600,
                        color: "#0F172A",
                        letterSpacing: "-0.005em",
                    }}
                >
                    {label}
                    <span aria-hidden="true">…</span>
                </div>
                <div
                    style={{
                        fontSize: 12,
                        color: "#64748B",
                        letterSpacing: "0.02em",
                    }}
                >
                    {subLabel}
                </div>
            </div>
            <style>{`
                @keyframes coda-fallback-spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
